import React from 'react'
import { FormikProps } from 'formik'
import { ModalBody, ModalFooter } from 'uiComponents/popups/modal.new'
import ActionButton from 'uiComponents/buttons'
import classNames from 'classnames'
import { PricingPlanFormValues, TWO_YEAR_TERM } from '../../pricingPlanFormSchema'
import { RadioOptionTile } from 'uiComponents/optionTile'
import { useCreateFormatCurrencyString } from 'uiComponents/money/useCurrency'
import './renewal.scss'
import { useTranslation } from 'translations'

interface PricingPlanRenewalProps {
    formik: FormikProps<PricingPlanFormValues>
    visible: boolean
    onBackClick: () => void
    onSubmit: () => void
}

export const PricingPlanRenewal = ({ formik, visible, onBackClick, onSubmit }: PricingPlanRenewalProps) => {
    const { t } = useTranslation()
    const formatCurrency = useCreateFormatCurrencyString()
    const {
        minimumTerm,
        allowYearlyInstallments,
        allowMonthlyInstallments,
        totalPriceYearly,
        totalPriceMonthly,
        pricePerYear,
        priceUpfront,
    } = formik.values

    const isTwoYearTerm = minimumTerm === TWO_YEAR_TERM

    const annualPrice = isTwoYearTerm ? pricePerYear ?? (priceUpfront != null ? priceUpfront / 2 : null) : totalPriceYearly

    const monthlyFromAnnual = annualPrice ? formatCurrency((annualPrice / 12).toFixed(2)) : null
    const monthlyFromMonthly = totalPriceMonthly ? formatCurrency((totalPriceMonthly / 12).toFixed(2)) : null

    const renewIntoSameDescription = (
        <>
            {allowYearlyInstallments && (
                <span>
                    {t('products.pricing.pricingPlans.renewalStep.annualContinue', {
                        price: formatCurrency(totalPriceYearly),
                    })}{' '}
                </span>
            )}
            {allowMonthlyInstallments && (
                <span>{t('products.pricing.pricingPlans.renewalStep.monthlyContinue', { price: monthlyFromMonthly })}</span>
            )}
        </>
    )

    const renewIntoMonthlyDescription = (
        <>
            {allowYearlyInstallments && (
                <span>
                    {t('products.pricing.pricingPlans.renewalStep.annualSwitch', { price: monthlyFromAnnual })}{' '}
                </span>
            )}
            {allowMonthlyInstallments && (
                <span>{t('products.pricing.pricingPlans.renewalStep.monthlyContinue', { price: monthlyFromMonthly })}</span>
            )}
        </>
    )

    const renewIntoOneYearDescription = annualPrice ? (
        <span>
            {t('products.pricing.pricingPlans.renewalStep.twoYearContinue', { price: formatCurrency(annualPrice) })}
        </span>
    ) : (
        <span>{t('products.pricing.pricingPlans.renewalStep.twoYearContinueNoPrice')}</span>
    )

    const doNotRenewDescription = isTwoYearTerm
        ? t('products.pricing.pricingPlans.renewalStep.doNotRenewTwoYear')
        : t('products.pricing.pricingPlans.renewalStep.doNotRenewOneYear')

    return (
        <div className={classNames('form-step pricing-plan-renewal', { visible })}>
            <ModalBody>
                <div className="renewal-options">
                    <RadioOptionTile
                        title={t('products.pricing.pricingPlans.renewalStep.doNotRenew')}
                        description={doNotRenewDescription}
                        checked={!formik.values.renewAutomatically}
                        onClick={() => {
                            formik.setFieldValue('renewAutomatically', false)
                            formik.setFieldValue('renewIntoMonthly', false)
                        }}
                    />
                    {isTwoYearTerm ? (
                        <RadioOptionTile
                            title={t('products.pricing.pricingPlans.renewalStep.renewIntoOneYear')}
                            description={renewIntoOneYearDescription}
                            checked={formik.values.renewAutomatically}
                            onClick={() => {
                                formik.setFieldValue('renewAutomatically', true)
                                formik.setFieldValue('renewIntoMonthly', false)
                            }}
                        />
                    ) : (
                        <>
                            <RadioOptionTile
                                title={t('products.pricing.pricingPlans.renewalStep.renewIntoSame')}
                                description={renewIntoSameDescription}
                                checked={formik.values.renewAutomatically && !formik.values.renewIntoMonthly}
                                onClick={() => {
                                    formik.setFieldValue('renewAutomatically', true)
                                    formik.setFieldValue('renewIntoMonthly', false)
                                }}
                            />
                            {allowYearlyInstallments && (
                                <RadioOptionTile
                                    title={t('products.pricing.pricingPlans.renewalStep.renewIntoMonthly')}
                                    description={renewIntoMonthlyDescription}
                                    checked={formik.values.renewAutomatically && formik.values.renewIntoMonthly}
                                    onClick={() => {
                                        formik.setFieldValue('renewAutomatically', true)
                                        formik.setFieldValue('renewIntoMonthly', true)
                                    }}
                                />
                            )}
                        </>
                    )}
                </div>
                <div className="explanation">
                    {isTwoYearTerm
                        ? t('products.pricing.pricingPlans.renewalStep.explanationTwoYear')
                        : t('products.pricing.pricingPlans.renewalStep.explanationOneYear')}
                </div>
            </ModalBody>
            <ModalFooter>
                <ActionButton kind="settings" secondary size="large" onClick={onBackClick}>
                    {t('common.back')}
                </ActionButton>
                <ActionButton size="large" onClick={onSubmit}>
                    {t('common.save')}
                </ActionButton>
            </ModalFooter>
        </div>
    )
}
